import React, { useState, useEffect, useRef } from 'react';
import {
  Modal, Form, Input, Select, DatePicker,
  Button, Table, Tooltip, message, Space, Pagination
} from 'antd';
import { SearchOutlined, ClearOutlined, FileExcelOutlined } from '@ant-design/icons';
import FileTextOutlined from '@ant-design/icons/FileTextOutlined.js';
import FyzksInput from '../../components/FyzksInput';
import { makePostCall, makeGetCall, getUserDetails, formatAgeShort } from '../../utils/helper';
import { getPatientIdLabel } from '../../constants/fieldLabels';
import PdfViewer from '../../components/PdfViewer';
import './ReportSearch.css';

const { RangePicker } = DatePicker;
interface ReportSearchProps {
  visible: boolean;
  onCancel: () => void;
}

const stripHtml = (html: string): string => {
  if (!html) return '';
  return html
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/\s+/g, ' ')
    .trim();
};

const buildSnippet = (html: string, header: string, keyword: string): string => {
  const fullText = (header || '') + ' ' + (html || '');
  if (!fullText.trim()) return '';
  const plain = stripHtml(fullText);
  if (!keyword) return plain.substring(0, 220);
  const lower = plain.toLowerCase();
  const idx = lower.indexOf(keyword.toLowerCase());
  if (idx === -1) return '';
  const start = Math.max(0, idx - 90);
  const end = Math.min(plain.length, idx + keyword.length + 130);
  return (start > 0 ? '…' : '') + plain.substring(start, end) + (end < plain.length ? '…' : '');
};

const HighlightedSnippet: React.FC<{ text: string; keyword: string }> = ({ text, keyword }) => {
  if (!keyword || !text) return <span className="rs-snippet-text">{text}</span>;
  const escaped = keyword.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escaped})`, 'gi'));
  return (
    <span className="rs-snippet-text">
      {parts.map((part, i) =>
        part.toLowerCase() === keyword.toLowerCase()
          ? <mark key={i} className="rs-highlight">{part}</mark>
          : part
      )}
    </span>
  );
};

const ReportSearch: React.FC<ReportSearchProps> = ({ visible, onCancel }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any[]>([]);
  const [searchedKeyword, setSearchedKeyword] = useState('');
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const [totalResults, setTotalResults] = useState(0);
  const lastSearchValues = useRef<any>(null);

  const [options, setOptions] = useState<{ bodyParts: any[]; modalities: any[]; centers: any[] }>({
    bodyParts: [],
    modalities: [],
    centers: []
  });

  const [pdfLoading, setPdfLoading] = useState<string | null>(null);
  const [pdfModal, setPdfModal] = useState<{ visible: boolean; pdfBuffer: any; record: any }>({
    visible: false, pdfBuffer: null, record: null
  });

  useEffect(() => {
    if (visible) fetchOptions();
  }, [visible]);

  const fetchOptions = async () => {
    if (options.bodyParts.length > 0) return; // Use cached options

    try {
      const [bpRes, modRes, centerRes] = await Promise.all([
        makeGetCall('/body-parts'),
        makeGetCall('/modalities'),
        makeGetCall('/center-options')
      ]);
      const bodyPartsRaw = bpRes.data?.data || [];
      const uniqueBodyParts = Array.from(new Map(bodyPartsRaw.map((item: any) => [item.value, item])).values());

      setOptions({
        bodyParts:  uniqueBodyParts,
        modalities: modRes.data?.data   || [],
        centers:    centerRes.data?.data || []
      });
    } catch (e) {
      console.error('Failed to fetch dropdown options', e);
    }
  };

  const fetchResults = async (values: any, page: number, pageSize: number) => {
    setLoading(true);
    try {
      const payload = {
        keyword:    values.keyword,
        body_part:  values.body_part ? values.body_part : undefined,
        sex:        values.sex,
        start_date: values.dateRange?.[0]?.format('YYYY-MM-DD'),
        end_date:   values.dateRange?.[1]?.format('YYYY-MM-DD'),
        center:     values.center,
        modalities: values.modalities,
        page_no: page - 1,
        limit: pageSize
      };
      const res = await makePostCall('/advanced-search-reports', payload);
      let data = res.data?.data || [];
      const total = res.data?.total || 0;
      if (values.keyword) {
        data.forEach((rec: any) => {
          rec._snippet = buildSnippet(rec.pr_report_html, rec.pr_report_header, values.keyword);
        });
      }

      setResults(data);
      setTotalResults(total);
      if (data.length === 0 && page === 1) message.info('No signed-off reports found matching your criteria.');
    } catch (e) {
      console.error('Failed to search reports', e);
      message.error('Failed to search reports.');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async (values: any) => {
    setSearchedKeyword(values.keyword || '');
    lastSearchValues.current = values;
    setPagination({ current: 1, pageSize: pagination.pageSize });
    fetchResults(values, 1, pagination.pageSize);
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setPagination({ current: page, pageSize });
    if (lastSearchValues.current) {
      fetchResults(lastSearchValues.current, page, pageSize);
    }
  };

  const handleClearSearch = () => {
    form.resetFields();
    setResults([]);
    setSearchedKeyword('');
    setTotalResults(0);
    setPagination({ current: 1, pageSize: pagination.pageSize });
    lastSearchValues.current = null;
  };

  const handleExportExcel = () => {
    if (!results.length) { message.warning('No data to export.'); return; }
    try {
      const headers = ['S.No', 'Patient Name', getPatientIdLabel(), 'Age', 'Sex', 'Body Part', 'Modality', 'Scan Date/Time', 'Site', 'Radiologist', 'Report Snippet'];
      const rows = results.map((rec: any, idx: number) => [
        idx + 1,
        rec.pacs_order?.patient?.pat_name   || '',
        rec.pacs_order?.patient?.pat_pin    || '',
        rec.pacs_order?.patient?.pat_age    || '',
        rec.pacs_order?.patient?.pat_sex    || '',
        rec.pacs_order?.po_body_part        || '',
        rec.pacs_order?.po_modality         || '',
        rec.pacs_order?.po_req_time
          ? new Date(rec.pacs_order.po_req_time).toLocaleString('en-IN') : '',
        rec.pacs_order?.po_center_name      || '',
        rec.pr_signed_by                    || '',
        searchedKeyword ? buildSnippet(rec.pr_report_html, rec.pr_report_header, searchedKeyword) : ''
      ]);

      const csvRows = [headers, ...rows].map(row =>
        row.map((cell: any) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')
      );
      const csvContent = '\uFEFF' + csvRows.join('\n');
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `report-search-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      message.success('Exported successfully.');
    } catch (e) {
      message.error('Failed to export.');
    }
  };

  const handleViewReport = async (record: any) => {
    const key = String(record.pr_id);
    setPdfLoading(key);
    try {
      const res = await makePostCall('/print-acc-report', {
        order_id: record.pacs_order?.pacs_ord_id,
        user_id:  getUserDetails()?.username,
      }, { responseType: 'arraybuffer' });
      setPdfModal({ visible: true, pdfBuffer: res.data, record });
    } catch (e) {
      console.error('Failed to fetch PDF', e);
      message.error('Failed to load report PDF.');
    } finally {
      setPdfLoading(null);
    }
  };

  const columns = [
    {
      title: 'S.No.', width: 45,
      render: (_: any, __: any, i: number) => <span className="rs-cell">{(pagination.current - 1) * pagination.pageSize + i + 1}</span>
    },
    {
      title: 'Patient Name', dataIndex: ['pacs_order', 'patient', 'pat_name'],
      ellipsis: true, width: 130,
      render: (v: string) => <span className="rs-cell">{v || '-'}</span>
    },
    {
      title: getPatientIdLabel(), dataIndex: ['pacs_order', 'patient', 'pat_pin'],
      width: 100,
      render: (v: string) => <span className="rs-cell">{v || '-'}</span>
    },
    {
      title: 'Age', dataIndex: ['pacs_order', 'patient', 'pat_age'], width: 48,
      render: (v: string, rec: any) => (
        <span className="rs-cell">
          {formatAgeShort(rec?.pacs_order?.patient?.pat_dob, v) || '-'}
        </span>
      )
    },
    {
      title: 'Sex', dataIndex: ['pacs_order', 'patient', 'pat_sex'], width: 44,
      render: (v: string) => <span className="rs-cell">{v || '-'}</span>
    },
    {
      title: 'Body Part', dataIndex: ['pacs_order', 'po_body_part'],
      ellipsis: true, width: 95,
      render: (v: string) => <span className="rs-cell">{v || '-'}</span>
    },
    {
      title: 'Modality', dataIndex: ['pacs_order', 'po_modality'], width: 70,
      render: (v: string) => <span className="rs-cell">{v || '-'}</span>
    },
    {
      title: 'Scan Date/Time', width: 130,
      render: (_: any, rec: any) => (
        <span className="rs-cell">
          {rec.pacs_order?.po_req_time
            ? new Date(rec.pacs_order.po_req_time).toLocaleString('en-IN', {
                day: '2-digit', month: 'short', year: 'numeric',
                hour: '2-digit', minute: '2-digit'
              })
            : '-'}
        </span>
      )
    },
    {
      title: 'Site', dataIndex: ['pacs_order', 'po_center_name'],
      ellipsis: true, width: 100,
      render: (v: string) => <span className="rs-cell">{v || '-'}</span>
    },
    {
      title: 'Radiologist', dataIndex: 'pr_signed_by',
      ellipsis: true, width: 105,
      render: (v: string) => <span className="rs-cell">{v || '-'}</span>
    },
    {
      title: 'View', fixed: 'right' as const, width: 50,
      render: (_: any, record: any) => (
        <Tooltip>
          <Button
            title='View Report'
            type="text" size="small"
            loading={pdfLoading === String(record.pr_id)}
            icon={<FileTextOutlined style={{ color: '#1890ff', fontSize: 14 }} />}
            onClick={() => handleViewReport(record)}
          />
        </Tooltip>
      )
    }
  ];
  const expandedRowRender = (record: any) => {
    const snippet = record._snippet || buildSnippet(record.pr_report_html, record.pr_report_header, searchedKeyword);
    if (!snippet) return null;
    return (
      <div className="rs-expanded-row">
        <HighlightedSnippet text={snippet} keyword={searchedKeyword} />
      </div>
    );
  };
  // Auto-expand all rows so the keyword excerpt is always visible as proof
  const expandedKeys = React.useMemo(() => {
    return searchedKeyword
      ? results.filter(r => !!r.pr_report_html).map(r => r.pr_id)
      : [];
  }, [searchedKeyword, results]);

  return (
    <>
      <Modal
        title={null}
        open={visible}
        onCancel={onCancel}
        footer={null}
        width={1260}
        style={{ top: 14 }}
        className="report-search-modal"
      >
        <div className="rs-modal-header">
          <span className="rs-modal-title">Report Search</span>
        </div>
        {/* ── Form + Results ── */}
        <div className="rs-body">
          <Form form={form} layout="vertical" onFinish={handleSearch} className="rs-form">
            {/* Row 1 */}
            <div className="rs-filters-row">
              <Form.Item name="keyword" className="rs-field-keyword">
                <Input
                  placeholder="Keyword"
                  allowClear
                  required
                  prefix={<SearchOutlined style={{ color: '#bfbfbf' }} />}
                  className="rs-input"
                />
              </Form.Item>
              <Form.Item name="body_part">
                <FyzksInput
                  placeholder="Body Part"
                  allowClear
                />
              </Form.Item>
              <Form.Item name="modalities">
                <Select
                  mode="multiple"
                  options={options.modalities}
                  placeholder="Modalities"
                  allowClear
                  maxTagCount="responsive"
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
            </div>
            {/* Row 2 */}
            <div className="rs-filters-row">
              <Form.Item name="center">
                <Select
                  options={options.centers}
                  placeholder="Center"
                  allowClear showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
              <Form.Item name="dateRange">
                <RangePicker placeholder={['From Date', 'To Date']} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="sex" className="rs-field-sex">
                <Select allowClear placeholder="Sex">
                  <Select.Option value="M">Male</Select.Option>
                  <Select.Option value="F">Female</Select.Option>
                  <Select.Option value="O">Other</Select.Option>
                </Select>
              </Form.Item>
              <div className="rs-btn-row">
                <Space size={8}>
                  <Button
                    type="primary" htmlType="submit" size="small"
                    icon={<SearchOutlined />} loading={loading}
                    className="rs-btn-search"
                  >
                    Search
                  </Button>
                  <Button
                    size="small" icon={<ClearOutlined />}
                    onClick={handleClearSearch} className="rs-btn-clear"
                  >
                    Clear
                  </Button>
                  <Button
                    size="small" icon={<FileExcelOutlined />}
                    onClick={handleExportExcel}
                    disabled={!results.length}
                    className="rs-btn-excel"
                  >
                    Excel
                  </Button>
                </Space>
                {totalResults > 0 && (
                  <span className="rs-result-count">
                    {(pagination.current - 1) * pagination.pageSize + 1}-{Math.min(pagination.current * pagination.pageSize, totalResults)} of {totalResults}
                  </span>
                )}
              </div>
            </div>
          </Form>

          <div className="rs-table-wrapper">
            <Table
              dataSource={results}
              columns={columns}
              rowKey="pr_id"
              loading={loading}
              size="small"
              scroll={{ x: 1060, y: 380 }}
              pagination={false}
              className="rs-table"
              locale={{ emptyText: 'Run a search to see signed-off reports' }}
              expandable={
                searchedKeyword
                  ? {
                      expandedRowRender,
                      expandedRowKeys: expandedKeys,
                      showExpandColumn: false,
                      rowExpandable: (rec: any) => !!rec.pr_report_html,
                    }
                  : undefined
              }
            />
            {totalResults > 0 && (
              <div className="rs-pagination-row">
                <Pagination
                  current={pagination.current}
                  pageSize={pagination.pageSize}
                  total={totalResults}
                  showSizeChanger={false}
                  size="small"
                  onChange={handlePageChange}
                />
              </div>
            )}
          </div>
        </div>
      </Modal>

      <Modal
        open={pdfModal.visible}
        onCancel={() => setPdfModal({ visible: false, pdfBuffer: null, record: null })}
        footer={null}
        width={920}
        style={{ top: 20 }}
        styles={{ body: { height: '80vh', padding: 0 } }}
      >
        {pdfModal.pdfBuffer && (
          <PdfViewer
            pdfArrayBuffer={pdfModal.pdfBuffer}
            order_id={pdfModal.record?.pacs_order?.pacs_ord_id}
            patientData={pdfModal.record}
          />
        )}
      </Modal>
    </>
  );
};

export default ReportSearch;
